<script setup lang="ts">
import { useCoreStore } from '@apps/main/new-desktop/core/stores';
import { getBoardBasisText } from '@apps/main/new-desktop/core/helpers/translation.helper';
import { CustomersRatingTag } from '@apps/main/new-desktop/modules/customersRating';
import { HotelAmenities } from '@apps/main/new-desktop/modules/hotelAmenities';
import { BoardBasisTag } from '@apps/main/new-desktop/modules/boardBasisTag';

import { AppRawButton, AppStars, AppSwiperGallery } from '@libs/ui';

import DateCardTag from '../../DateCardTag.vue';
import type {
  AccommodationUpsellCardData,
  AccommodationUpsellSegmentContext,
} from '../../../../models';

interface AccommodationUpsellCardProps {
  data: AccommodationUpsellCardData;
  contex: AccommodationUpsellSegmentContext;
}

const props = defineProps<AccommodationUpsellCardProps>();

const coreStore = useCoreStore();

interface AccommodationUpsellCardEmits {
  (eventName: 'open-hotel-details', hotelCode: string): void;
}

const emit = defineEmits<AccommodationUpsellCardEmits>();
</script>

<template>
  <AppRawButton
    class="accommodation-upsell-card"
    @click="emit('open-hotel-details', props.data.hotelCode)"
  >
    <div class="upper-section">
      <div class="image-wrapper">
        <AppSwiperGallery
          :image-urls="props.data.imgUrls"
          :show-nav-buttons="true"
          :display-gallery-dots="false"
          :allow-full-gallery="false"
          :error-image-handler="
            (src) =>
              coreStore.handleImageLoadError({
                src: src,
                source: 'accommodation-upsell-card-swiper-gallery',
              })
          "
          slides-amount-position="right"
          slides-amount-size="s"
          :left-arrow-aria-label="$t('a11y.previous_image')"
          :right-arrow-aria-label="$t('a11y.next_image')"
          :full-gallery-aria-label="$t('a11y.open_image_gallery')"
        />
        <DateCardTag :date="props.data.date" />
      </div>
    </div>
    <div class="lower-section">
      <span class="hotel-name">{{ props.data.hotelName }}</span>
      <span class="hotel-destination">{{ props.data.hotelDestination }}</span>
      <div class="rating-wrapper">
        <AppStars :amount="props.data.starRating" size="18" />
        <CustomersRatingTag
          v-if="props.data.guestRating"
          :guest-rating="props.data.guestRating"
        />
      </div>
      <div class="amenities-wrapper">
        <HotelAmenities :amenities="[props.data.amenities]" />
      </div>
      <BoardBasisTag
        v-if="props.data.boardBasis"
        class="board-basis"
        :board-basis-code="props.data.boardBasis"
        :translations-callback="getBoardBasisText"
      />

      <div class="price-wrapper">
        <span class="label">
          {{ $t('planner.price_for_this_accommodation') }}
        </span>

        <span class="value">
          {{ props.data.price }}
        </span>
      </div>
    </div>
  </AppRawButton>
</template>

<style scoped lang="scss">
.accommodation-upsell-card {
  width: 343px;
  max-width: calc(100vw - 3 * var(--spacing-16));
  height: 392px;
  padding: var(--spacing-12);
  border-radius: var(--border-radius-12);
  border: 1px solid var(--color-brand-lilac);
  background-color: var(--color-white);

  display: flex;
  flex-direction: column;
  gap: var(--spacing-12);
  cursor: pointer;

  .image-wrapper {
    border-radius: var(--border-radius-8);
    overflow: hidden;
    position: relative;
    height: 200px;
  }

  .lower-section {
    display: flex;
    flex-direction: column;
    color: var(--color-neutral-70);
    flex: 1;

    .hotel-name {
      @include font-label-m-bold;
      margin-bottom: var(--spacing-4);
    }

    .hotel-destination {
      @include font-label-s-light;
      margin-bottom: var(--spacing-8);
    }

    .hotel-name,
    .hotel-destination {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .rating-wrapper {
      display: flex;
      gap: var(--spacing-12);
      align-items: center;
      margin-bottom: var(--spacing-8);
    }

    .amenities-wrapper {
      margin-bottom: var(--spacing-8);
    }

    .board-basis {
      margin-bottom: var(--spacing-12);
    }

    .price-wrapper {
      margin-top: auto;
      display: flex;
      justify-content: space-between;
      align-items: center;

      .label {
        @include font-label-s-light;
      }

      .value {
        @include font-label-xl-bold;
      }
    }
  }
}
</style>
